import { useEffect, useState } from 'react'

import { Button, Combobox, Input } from '@/components/ui'
import FormGroup from '@/core/components/FormGroup'
import { AvailableTokenPool } from '@/common/models/gamification'
import { createTokenPool, getTokenPools } from '@/core/services/gamification'

interface TokenPoolSelectProps {
  editionId: string
  value?: string
  onChange: (poolId: string | undefined) => void
}

/** Sentinel Combobox value for "no pool" — Combobox options can't share the empty string. */
const NONE_VALUE = 'NONE'

/**
 * Optional token-pool picker for the CONTINUOUS_TOPUP / CONTINUOUS_SPENDING quest editors. Fetches
 * the edition's pools itself, offers a "None" option (maps to `undefined`), and lets the author
 * create a new pool inline without leaving the quest editor.
 */
export const TokenPoolSelect = ({
  editionId,
  value,
  onChange,
}: TokenPoolSelectProps) => {
  const [pools, setPools] = useState<AvailableTokenPool[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const [showAdd, setShowAdd] = useState(false)
  const [newName, setNewName] = useState('')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | undefined>()

  const load = async () => {
    if (!editionId) return
    setLoading(true)
    setError(undefined)
    try {
      const { data } = await getTokenPools(editionId)
      setPools(data ?? [])
    } catch {
      setError('Failed to load token pools')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editionId])

  const handleValueChange = (v: string) => {
    onChange(v === NONE_VALUE || v === '' ? undefined : v)
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) {
      setCreateError('Name is required')
      return
    }
    setCreating(true)
    setCreateError(undefined)
    try {
      const { data } = await createTokenPool(editionId, { name, active: true })
      await load()
      onChange(data.id)
      setNewName('')
      setShowAdd(false)
    } catch {
      setCreateError('Failed to create pool')
    } finally {
      setCreating(false)
    }
  }

  const options = [
    { value: NONE_VALUE, label: 'None (award points directly)' },
    ...pools.map((p) => ({ value: p.id, label: p.name })),
  ]

  return (
    <FormGroup label="Token pool (optional)">
      <div className="w-full space-y-2">
        <Combobox
          options={options}
          value={value ?? NONE_VALUE}
          onValueChange={handleValueChange}
          placeholder="None (award points directly)"
          searchPlaceholder="Search pools..."
          isLoading={loading}
          isInvalid={!!error}
        />
        {error && <span className="block text-xs text-accent-3">{error}</span>}

        {showAdd ? (
          <div className="flex items-start gap-2">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="New pool name"
              isInvalid={!!createError}
            />
            <Button
              type="button"
              variant="primary-bordered"
              onClick={handleCreate}
              disabled={creating}
            >
              Add
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowAdd(false)
                setNewName('')
                setCreateError(undefined)
              }}
              disabled={creating}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <a
            className="text-accent-1 underline text-sm"
            href="#"
            onClick={(e) => {
              e.preventDefault()
              setShowAdd(true)
            }}
          >
            ＋ New pool
          </a>
        )}
        {createError && (
          <span className="block text-xs text-accent-3">{createError}</span>
        )}

        <span className="block text-xs text-monochrome-2">
          When set, a top-up quest banks tokens into the user's pool balance (no
          points); a spending quest earns points only for what's drawn from the
          pool. Pools are shared across quests.
        </span>
      </div>
    </FormGroup>
  )
}
